import { useMobileLayoutState } from '@src/hooks/useMobileLayoutState';
import { DESKTOP_ON_MOBILE_INITIAL_SCALE } from '@src/hooks/useMobileLayoutViewport';
import {
  Button,
  ButtonVariants,
  Flex,
  FormControlSizes,
  MixpanelEvent,
  MixpanelEventSource,
  Text,
  useMixpanel,
} from '@talos/kyoko';
import { startTransition, useCallback } from 'react';
import styled from 'styled-components';

function cssPxForVisualSize(visualPx: number): number {
  return Math.round(visualPx / DESKTOP_ON_MOBILE_INITIAL_SCALE);
}

const BANNER_PADDING_Y_PX = cssPxForVisualSize(8);
const BANNER_PADDING_X_PX = cssPxForVisualSize(16);
const BANNER_GAP_PX = cssPxForVisualSize(8);
const BANNER_FONT_SIZE_PX = cssPxForVisualSize(14);
const BANNER_BUTTON_HEIGHT_PX = cssPxForVisualSize(32);
const BANNER_BUTTON_FONT_SIZE_PX = cssPxForVisualSize(14);
const BANNER_BUTTON_PADDING_Y_PX = cssPxForVisualSize(4);
const BANNER_BUTTON_PADDING_X_PX = cssPxForVisualSize(8);

export function ForcedDesktopModeBanner() {
  const mixpanel = useMixpanel(MixpanelEventSource.ForcedDesktopBanner);
  const { isDesktopLayoutForcedForSession, setIsMobileLayoutDisabledForSession } = useMobileLayoutState();

  const handleUseMobileVersion = useCallback(() => {
    mixpanel.track(MixpanelEvent.ReenableMobileUI);
    startTransition(() => setIsMobileLayoutDisabledForSession(false));
  }, [mixpanel, setIsMobileLayoutDisabledForSession]);

  if (!isDesktopLayoutForcedForSession) {
    return null;
  }

  return (
    <BannerWrapper data-testid="forced-desktop-mode-banner">
      <Text color="colorTextPrimaryButton" weight="fontWeightMedium">
        You&apos;re using the desktop version
      </Text>
      <UseMobileVersionButton
        data-testid="forced-desktop-use-mobile-version-button"
        height={BANNER_BUTTON_HEIGHT_PX}
        onClick={handleUseMobileVersion}
        size={FormControlSizes.Large}
        variant={ButtonVariants.Default}
      >
        Return to mobile version
      </UseMobileVersionButton>
    </BannerWrapper>
  );
}

const BannerWrapper = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.backgroundPrimary};
  box-sizing: border-box;
  flex-shrink: 0;
  flex-wrap: wrap;
  font-size: ${BANNER_FONT_SIZE_PX}px;
  gap: ${BANNER_GAP_PX}px;
  justify-content: center;
  line-height: 1.3;
  padding: ${BANNER_PADDING_Y_PX}px ${BANNER_PADDING_X_PX}px;
  width: 100%;
`;

const UseMobileVersionButton = styled(Button)`
  font-size: ${BANNER_BUTTON_FONT_SIZE_PX}px;
  padding: ${BANNER_BUTTON_PADDING_Y_PX}px ${BANNER_BUTTON_PADDING_X_PX}px;
`;
